import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';

import { EmailQueueService } from '../../../core/admin/email-queue.service';
import { HeldEmailDetail } from '../../../core/admin/email-queue.types';
import { ModalComponent } from '../../../shared/modal/modal';

/** The email's HTML with its links dead: no target for a click or the Enter key. */
export function withoutLinks(html: string): string {
  return html.replace(/\shref\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '');
}

/**
 * Read a held email before deciding. Its links are switched off: one may be the
 * customer's single-use password link, and following it here would spend it.
 */
@Component({
  selector: 'app-email-preview',
  imports: [ModalComponent],
  template: `
    <app-modal heading="Email preview">
      @if (email(); as e) {
        <dl class="preview-facts">
          <dt>To</dt>
          <dd>{{ e.to_email }}</dd>
          <dt>From</dt>
          <dd>{{ e.from_email }}</dd>
          <dt>Subject</dt>
          <dd>{{ e.subject }}</dd>
          @if (e.attachments.length) {
            <dt>Attachments</dt>
            <dd>{{ e.attachments.join(', ') }}</dd>
          }
        </dl>
        @if (e.html_body) {
          <!-- Angular's [innerHTML] binding sanitizes by default. -->
          <div class="preview-body" [innerHTML]="body(e)"></div>
          <p class="preview-note">Links are switched off in this preview.</p>
        } @else {
          <p class="preview-note">Content no longer retained.</p>
        }
      } @else if (failed()) {
        <p role="alert">Couldn't load this email.</p>
      } @else {
        <p aria-live="polite">Loading…</p>
      }
    </app-modal>
  `,
  styles: `
    .preview-facts {
      display: grid;
      grid-template-columns: max-content 1fr;
      gap: var(--space-1) var(--space-4);
      margin: 0 0 var(--space-4);
      font-size: var(--font-size-sm);
    }
    dt {
      color: var(--muted-foreground);
    }
    dd {
      margin: 0;
      overflow-wrap: anywhere;
    }
    .preview-body {
      padding: var(--space-4);
      border: 1px solid var(--border);
      border-radius: var(--radius-md);
      overflow-x: auto;
    }
    .preview-note {
      margin: var(--space-2) 0 0;
      font-size: var(--font-size-xs);
      color: var(--muted-foreground);
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmailPreviewComponent implements OnInit {
  private readonly service = inject(EmailQueueService);
  private readonly id = inject<string>(MAT_DIALOG_DATA);

  protected readonly email = signal<HeldEmailDetail | null>(null);
  protected readonly failed = signal(false);

  protected body(email: HeldEmailDetail): string {
    return withoutLinks(email.html_body);
  }

  ngOnInit(): void {
    this.service.detail(this.id).subscribe({
      next: (e) => this.email.set(e),
      error: () => this.failed.set(true),
    });
  }
}
